// Service for fetching data from Laravel backend
import { config } from '../config';

const BACKEND_URL = config.BACKEND_URL;

class DataService {
  constructor() {
    this.cache = {
      categories: null,
      items: null,
      pricingConfig: null,
      linenTypes: null,
      lastFetch: null,
    };
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  // Check if cache is valid
  isCacheValid() {
    return this.cache.lastFetch && 
           (Date.now() - this.cache.lastFetch) < this.cacheTimeout;
  }

  // Clear cache
  clearCache() {
    this.cache = {
      categories: null,
      items: null,
      pricingConfig: null,
      linenTypes: null,
      lastFetch: null,
    };
  }

  // Fetch all data at once (most efficient)
  async getAllData() {
    if (this.isCacheValid()) {
      return {
        categories: this.cache.categories,
        items: this.cache.items,
        pricingConfig: this.cache.pricingConfig,
        linenTypes: this.cache.linenTypes,
      };
    }

    try {
      const response = await fetch(`${BACKEND_URL}/items/all`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Update cache
      this.cache = {
        categories: data.categories,
        items: data.items,
        pricingConfig: data.pricingConfig,
        linenTypes: data.linenTypes,
        lastFetch: Date.now(),
      };

      return data;
    } catch (error) {
      console.error('Error fetching all data:', error);
      throw error;
    }
  }

  // Fetch categories only
  async getCategories() {
    if (this.isCacheValid() && this.cache.categories) {
      return this.cache.categories;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/items/categories`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.cache.categories = data.categories;
      this.cache.lastFetch = Date.now();
      
      return data.categories;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  // Fetch items only
  async getItems() {
    if (this.isCacheValid() && this.cache.items) {
      return this.cache.items;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/items`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.cache.items = data.items;
      this.cache.lastFetch = Date.now();
      
      return data.items;
    } catch (error) {
      console.error('Error fetching items:', error);
      throw error;
    }
  }

  // Fetch pricing configuration
  async getPricingConfig() {
    if (this.isCacheValid() && this.cache.pricingConfig) {
      return this.cache.pricingConfig;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/items/pricing`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.cache.pricingConfig = data.pricingConfig;
      this.cache.lastFetch = Date.now();
      
      return data.pricingConfig;
    } catch (error) {
      console.error('Error fetching pricing config:', error);
      throw error;
    }
  }

  // Fetch items by category
  async getItemsByCategory(categoryId) {
    try {
      const response = await fetch(`${BACKEND_URL}/items/category/${categoryId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.items;
    } catch (error) {
      console.error('Error fetching items by category:', error);
      throw error;
    }
  }

  // Get mock data as fallback
  getMockData() {
    return {
      categories: [
        { id: 1, name: 'sheets' },
        { id: 2, name: 'bedding' },
        { id: 3, name: 'towels' },
      ],
      items: [
        { id: 1, name: 'King Flat Sheet', slug: 'king-flat', category_id: 1 },
        { id: 2, name: 'Queen Flat Sheet', slug: 'queen-flat', category_id: 1 },
        // ... more items
      ],
      pricingConfig: {
        'king-flat': { cost: 28, rentalPrice: 9.33, launderingPrice: 4.50 },
        'queen-flat': { cost: 25, rentalPrice: 8.33, launderingPrice: 4.00 },
        // ... more pricing
      },
      linenTypes: [
        { id: 'king-flat', name: 'King Flat Sheet', category: 'sheets' },
        { id: 'queen-flat', name: 'Queen Flat Sheet', category: 'sheets' },
        // ... more types
      ],
    };
  }
}

export const dataService = new DataService(); 